import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { BODY_MODULE_TYPES } from '@/lib/furnitureData';
import ContextMenu from './ContextMenu';
const SCALE = 0.01;
const SNAP_SIZE = 5;

// Neutral "unfinished LDSP" color used when no material is assigned
const DEFAULT_BODY_COLOR = '#D8CAB1';

// Procedural canvas textures for furniture materials — avoids flaky external
// URLs and gives a consistent look regardless of network.
const materialTextureCache = {};
function makeMaterialTexture(material) {
  if (!material) return null;
  if (materialTextureCache[material]) return materialTextureCache[material];

  const size = 512;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');

  const drawWood = (base, mid, dark) => {
    const g = ctx.createLinearGradient(0, 0, size, 0);
    g.addColorStop(0, base);
    g.addColorStop(0.5, mid);
    g.addColorStop(1, base);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);

    for (let i = 0; i < 160; i++) {
      const y = Math.random() * size;
      ctx.strokeStyle = `rgba(0,0,0,${0.03 + Math.random() * 0.07})`;
      ctx.lineWidth = 0.4 + Math.random() * 1.2;
      ctx.beginPath();
      ctx.moveTo(0, y);
      let x = 0;
      while (x < size) {
        x += 6 + Math.random() * 18;
        ctx.lineTo(x, y + (Math.random() - 0.5) * 3.5);
      }
      ctx.stroke();
    }
    for (let i = 0; i < 6; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 4 + Math.random() * 10;
      const rg = ctx.createRadialGradient(x, y, 0, x, y, r);
      rg.addColorStop(0, dark);
      rg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = rg;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const drawBoard = (base, speckle) => {
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);
    const img = ctx.getImageData(0, 0, size, size);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const n = (Math.random() - 0.5) * 22;
      d[i] = Math.max(0, Math.min(255, d[i] + n));
      d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + n));
      d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + n));
    }
    ctx.putImageData(img, 0, 0);
    for (let i = 0; i < 900; i++) {
      ctx.fillStyle = `rgba(${speckle},${0.15 + Math.random() * 0.25})`;
      ctx.fillRect(Math.random() * size, Math.random() * size, 1 + Math.random() * 2, 1 + Math.random() * 2);
    }
  };

  const drawMetal = () => {
    const g = ctx.createLinearGradient(0, 0, 0, size);
    g.addColorStop(0, '#D8DCE0');
    g.addColorStop(0.5, '#B7BCC2');
    g.addColorStop(1, '#9CA1A7');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 450; i++) {
      ctx.strokeStyle = `rgba(255,255,255,${0.03 + Math.random() * 0.08})`;
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      const y = Math.random() * size;
      ctx.moveTo(0, y);
      ctx.lineTo(size, y + (Math.random() - 0.5) * 2);
      ctx.stroke();
    }
  };

  switch (material) {
    case 'solid_wood':
      drawWood('#B98352', '#8E5A2B', 'rgba(60,30,10,0.55)');
      break;
    case 'chipboard':
      drawBoard('#D7C7A8', '130,105,70');
      break;
    case 'shelving_ldsp_16':
      drawBoard('#E6D7B8', '140,115,75');
      break;
    case 'shelving_ldsp_22':
      drawBoard('#D9C39B', '135,105,70');
      break;
    case 'mdf':
      drawBoard('#E4D4B4', '150,120,80');
      break;
    case 'metal':
      drawMetal();
      break;
    case 'glass':
      return null;
    default:
      drawBoard('#D8CAB1', '150,125,90');
  }

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 4;
  tex.colorSpace = THREE.SRGBColorSpace;
  materialTextureCache[material] = tex;
  return tex;
}

// Chromed furniture handle (bar style) used on doors and drawers
function buildHandle({ length, isVertical, faceDepth }) {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: '#C8CCD2', metalness: 0.92, roughness: 0.28,
  });

  const L = Math.max(0.06, Math.min(length, 0.2));
  const barR = 0.007;
  const standOff = 0.022;
  const standR = 0.0055;
  const insetFromEnds = 0.012;

  const bar = new THREE.Mesh(
    new THREE.CylinderGeometry(barR, barR, L, 18),
    mat
  );
  if (!isVertical) bar.rotation.z = Math.PI / 2;
  bar.position.z = faceDepth / 2 + standOff;
  bar.castShadow = true;
  group.add(bar);

  const makeStand = () => {
    const s = new THREE.Mesh(
      new THREE.CylinderGeometry(standR, standR, standOff, 10),
      mat
    );
    s.rotation.x = Math.PI / 2;
    s.position.z = faceDepth / 2 + standOff / 2;
    s.castShadow = true;
    return s;
  };

  const s1 = makeStand();
  const s2 = makeStand();
  if (isVertical) {
    s1.position.y = L / 2 - insetFromEnds;
    s2.position.y = -L / 2 + insetFromEnds;
  } else {
    s1.position.x = L / 2 - insetFromEnds;
    s2.position.x = -L / 2 + insetFromEnds;
  }
  group.add(s1);
  group.add(s2);

  return group;
}

// Procedural ginger fur texture (diffuse + roughness with tabby stripes)
const furTextureCache = {};
function makeFurTexture(variant = 'main') {
  if (furTextureCache[variant]) return furTextureCache[variant];
  const c = document.createElement('canvas');
  c.width = 512; c.height = 512;
  const ctx = c.getContext('2d');

  const palette = variant === 'belly'
    ? { base: '#F6D3A8', mid: '#E8B07A', stripe: '#C98754' }
    : variant === 'dark'
    ? { base: '#A8491E', mid: '#8E3C18', stripe: '#5C2810' }
    : { base: '#E57A35', mid: '#C25F24', stripe: '#8A3E16' };

  const grad = ctx.createRadialGradient(256, 256, 40, 256, 256, 340);
  grad.addColorStop(0, palette.base);
  grad.addColorStop(1, palette.mid);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 512, 512);

  // Tabby wavy stripes (classic ginger pattern)
  if (variant !== 'belly') {
    ctx.strokeStyle = palette.stripe;
    for (let y = -20; y < 540; y += 26 + Math.random() * 18) {
      ctx.globalAlpha = 0.35 + Math.random() * 0.3;
      ctx.lineWidth = 6 + Math.random() * 6;
      ctx.beginPath();
      ctx.moveTo(-10, y);
      for (let x = 0; x <= 520; x += 8) {
        const wy = y + Math.sin((x + y * 0.5) * 0.02) * 9 + Math.sin(x * 0.08) * 3;
        ctx.lineTo(x, wy);
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // Individual fur hairs (short strokes)
  for (let i = 0; i < 3200; i++) {
    const x = Math.random() * 512;
    const y = Math.random() * 512;
    const len = 2 + Math.random() * 5;
    const ang = -Math.PI / 2 + (Math.random() - 0.5) * 0.5;
    const shade = Math.random();
    ctx.strokeStyle = shade > 0.6
      ? 'rgba(0,0,0,0.22)'
      : shade > 0.3
      ? 'rgba(255,220,170,0.18)'
      : `rgba(${60 + Math.random() * 60},${30 + Math.random() * 40},10,0.15)`;
    ctx.lineWidth = 0.5 + Math.random() * 0.9;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
    ctx.stroke();
  }

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  furTextureCache[variant] = tex;
  return tex;
}

// Deform a sphere into a pear/egg shape for anatomical silhouette
function deformEgg(geo, { bottomWiden = 1.25, topNarrow = 0.85, lean = 0 } = {}) {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const ny = y; // local y in [-r..+r]
    const factor = ny < 0
      ? 1 + Math.abs(ny) * (bottomWiden - 1) * 2
      : 1 - ny * (1 - topNarrow) * 1.4;
    pos.setX(i, x * factor);
    pos.setZ(i, z * factor + (ny > 0 ? lean * ny : 0));
  }
  geo.computeVertexNormals();
  return geo;
}

// Realistic ginger cat reference (~30 cm tall sitting)
function buildCatReference() {
  const group = new THREE.Group();
  group.name = 'cat-reference';
  group.userData.isReference = true;

  const furTex = makeFurTexture('main');
  const bellyTex = makeFurTexture('belly');

  const furMain = new THREE.MeshStandardMaterial({
    map: furTex, color: '#F3AE75', roughness: 0.92, metalness: 0.0,
  });
  const furBelly = new THREE.MeshStandardMaterial({
    map: bellyTex, color: '#FBE1BE', roughness: 0.95, metalness: 0.0,
  });
  const pink = new THREE.MeshStandardMaterial({ color: '#F4A9B5', roughness: 0.5, metalness: 0.0 });
  const pinkDark = new THREE.MeshStandardMaterial({ color: '#C97A84', roughness: 0.5, metalness: 0.0 });
  const irisMat = new THREE.MeshStandardMaterial({
    color: '#6BA84F', roughness: 0.25, metalness: 0.0, emissive: '#3E6930', emissiveIntensity: 0.15,
  });
  const pupilMat = new THREE.MeshStandardMaterial({ color: '#0A0A0A', roughness: 0.2, metalness: 0.0 });
  const shineMat = new THREE.MeshStandardMaterial({
    color: '#FFFFFF', roughness: 0.1, emissive: '#FFFFFF', emissiveIntensity: 0.7,
  });
  const noseMat = new THREE.MeshStandardMaterial({ color: '#C46A6A', roughness: 0.35, metalness: 0.0 });

  // ---------- Lower body (rump) — sitting, wide base ----------
  const rumpGeo = deformEgg(new THREE.SphereGeometry(0.085, 32, 28),
    { bottomWiden: 1.35, topNarrow: 0.75 });
  const rump = new THREE.Mesh(rumpGeo, furMain);
  rump.scale.set(1.0, 1.0, 1.15);
  rump.position.set(0, 0.09, -0.01);
  rump.castShadow = true;
  rump.receiveShadow = true;
  group.add(rump);

  // ---------- Chest / upper body ----------
  const chestGeo = deformEgg(new THREE.SphereGeometry(0.072, 32, 28),
    { bottomWiden: 0.9, topNarrow: 0.85, lean: 0.12 });
  const chest = new THREE.Mesh(chestGeo, furMain);
  chest.scale.set(1.05, 1.15, 0.95);
  chest.position.set(0, 0.165, 0.035);
  chest.castShadow = true;
  group.add(chest);

  // Belly (lighter, front-facing)
  const belly = new THREE.Mesh(new THREE.SphereGeometry(0.063, 24, 20), furBelly);
  belly.scale.set(0.95, 1.2, 0.55);
  belly.position.set(0, 0.115, 0.075);
  group.add(belly);

  // Back tabby darker streak
  const backStreak = new THREE.Mesh(
    new THREE.SphereGeometry(0.05, 24, 18),
    new THREE.MeshStandardMaterial({ map: makeFurTexture('dark'), color: '#A85B2C', roughness: 0.95 })
  );
  backStreak.scale.set(0.5, 1.6, 0.7);
  backStreak.position.set(0, 0.15, -0.058);
  group.add(backStreak);

  // ---------- Head ----------
  const headGeo = new THREE.SphereGeometry(0.062, 32, 28);
  // flatten slightly top/bottom for rounded-triangular silhouette
  const hp = headGeo.attributes.position;
  for (let i = 0; i < hp.count; i++) {
    const x = hp.getX(i), y = hp.getY(i), z = hp.getZ(i);
    hp.setY(i, y * (y > 0 ? 0.85 : 1.0));
    hp.setZ(i, z * (z < 0 ? 0.9 : 1.05));
    hp.setX(i, x * (Math.abs(y) > 0.03 ? 0.95 : 1.02));
  }
  headGeo.computeVertexNormals();
  const head = new THREE.Mesh(headGeo, furMain);
  head.position.set(0, 0.24, 0.07);
  head.castShadow = true;
  group.add(head);

  // Cheek pads (fluffy cheeks)
  const cheekGeo = new THREE.SphereGeometry(0.022, 20, 16);
  const cheekL = new THREE.Mesh(cheekGeo, furBelly);
  cheekL.scale.set(0.95, 0.85, 0.9);
  cheekL.position.set(-0.028, 0.22, 0.115);
  group.add(cheekL);
  const cheekR = cheekL.clone();
  cheekR.position.x = 0.028;
  group.add(cheekR);

  // Muzzle + chin (light)
  const muzzle = new THREE.Mesh(new THREE.SphereGeometry(0.022, 20, 16), furBelly);
  muzzle.scale.set(1.0, 0.7, 0.9);
  muzzle.position.set(0, 0.208, 0.12);
  group.add(muzzle);

  // ---------- Ears (rounded triangular + inner pink) ----------
  const earShape = new THREE.Shape();
  earShape.moveTo(-0.022, 0);
  earShape.quadraticCurveTo(-0.018, 0.045, 0, 0.055);
  earShape.quadraticCurveTo(0.018, 0.045, 0.022, 0);
  earShape.lineTo(-0.022, 0);
  const earGeo = new THREE.ExtrudeGeometry(earShape, { depth: 0.008, bevelEnabled: true, bevelSize: 0.004, bevelThickness: 0.003, bevelSegments: 3 });
  earGeo.translate(0, 0, -0.004);

  const earL = new THREE.Mesh(earGeo, furMain);
  earL.rotation.y = -0.35;
  earL.rotation.z = 0.15;
  earL.position.set(-0.042, 0.285, 0.048);
  earL.castShadow = true;
  group.add(earL);
  const earR = new THREE.Mesh(earGeo, furMain);
  earR.rotation.y = 0.35;
  earR.rotation.z = -0.15;
  earR.position.set(0.042, 0.285, 0.048);
  earR.castShadow = true;
  group.add(earR);

  // Inner ears (pink)
  const innerEarShape = new THREE.Shape();
  innerEarShape.moveTo(-0.012, 0.003);
  innerEarShape.quadraticCurveTo(-0.01, 0.032, 0, 0.04);
  innerEarShape.quadraticCurveTo(0.01, 0.032, 0.012, 0.003);
  innerEarShape.lineTo(-0.012, 0.003);
  const innerEarGeo = new THREE.ShapeGeometry(innerEarShape);

  const innerL = new THREE.Mesh(innerEarGeo, pink);
  innerL.rotation.y = -0.35;
  innerL.rotation.z = 0.15;
  innerL.position.set(-0.041, 0.285, 0.053);
  group.add(innerL);
  const innerR = new THREE.Mesh(innerEarGeo, pink);
  innerR.rotation.y = 0.35;
  innerR.rotation.z = -0.15;
  innerR.position.set(0.041, 0.285, 0.053);
  group.add(innerR);

  // ---------- Eyes (almond-shaped, slit pupils) ----------
  const buildEye = (side) => {
    const eyeGroup = new THREE.Group();
    // sclera / iris
    const iris = new THREE.Mesh(new THREE.SphereGeometry(0.011, 20, 16), irisMat);
    iris.scale.set(1.0, 0.75, 0.55);
    eyeGroup.add(iris);
    // vertical slit pupil
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.009, 16, 14), pupilMat);
    pupil.scale.set(0.25, 0.75, 0.35);
    pupil.position.set(0, 0, 0.003);
    eyeGroup.add(pupil);
    // catch-light
    const shine = new THREE.Mesh(new THREE.SphereGeometry(0.0028, 10, 8), shineMat);
    shine.position.set(side * 0.003, 0.004, 0.008);
    eyeGroup.add(shine);
    // eyeliner (dark outline)
    const linerGeo = new THREE.TorusGeometry(0.011, 0.0016, 6, 24);
    const liner = new THREE.Mesh(linerGeo,
      new THREE.MeshStandardMaterial({ color: '#1A0A06', roughness: 0.5 }));
    liner.scale.set(1.0, 0.7, 0.1);
    liner.position.z = 0.001;
    eyeGroup.add(liner);
    return eyeGroup;
  };
  const eyeL = buildEye(-1);
  eyeL.position.set(-0.023, 0.248, 0.118);
  eyeL.rotation.y = -0.12;
  eyeL.rotation.z = 0.08;
  group.add(eyeL);
  const eyeR = buildEye(1);
  eyeR.position.set(0.023, 0.248, 0.118);
  eyeR.rotation.y = 0.12;
  eyeR.rotation.z = -0.08;
  group.add(eyeR);

  // ---------- Nose (triangular) ----------
  const noseShape = new THREE.Shape();
  noseShape.moveTo(-0.006, 0.004);
  noseShape.lineTo(0.006, 0.004);
  noseShape.lineTo(0, -0.007);
  noseShape.lineTo(-0.006, 0.004);
  const noseGeo = new THREE.ExtrudeGeometry(noseShape, { depth: 0.004, bevelEnabled: true, bevelSize: 0.0015, bevelThickness: 0.001, bevelSegments: 2 });
  const nose = new THREE.Mesh(noseGeo, noseMat);
  nose.position.set(0, 0.222, 0.138);
  group.add(nose);

  // Mouth line (two curves)
  const mouthMat = new THREE.LineBasicMaterial({ color: 0x2A0F06, transparent: true, opacity: 0.85 });
  const mPts1 = [];
  for (let t = 0; t <= 1; t += 0.1) {
    const x = -t * 0.012;
    const y = 0.212 - Math.sin(t * Math.PI) * 0.004;
    mPts1.push(new THREE.Vector3(x, y, 0.138));
  }
  const mPts2 = mPts1.map(p => new THREE.Vector3(-p.x, p.y, p.z));
  group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(mPts1), mouthMat));
  group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(mPts2), mouthMat));

  // ---------- Front legs (sitting, straight down) ----------
  const legCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 0.1, 0),
    new THREE.Vector3(0, 0.06, 0.015),
    new THREE.Vector3(0, 0.02, 0.035),
  ]);
  const legGeo = new THREE.TubeGeometry(legCurve, 16, 0.019, 14, false);
  const legL = new THREE.Mesh(legGeo, furMain);
  legL.position.set(-0.035, 0, 0.09);
  legL.castShadow = true;
  const legR = legL.clone();
  legR.position.x = 0.035;
  group.add(legL, legR);

  // Paws (rounded, with subtle toes)
  const pawGeo = new THREE.SphereGeometry(0.024, 20, 16);
  const pawL = new THREE.Mesh(pawGeo, furMain);
  pawL.scale.set(1.0, 0.7, 1.25);
  pawL.position.set(-0.035, 0.012, 0.122);
  pawL.castShadow = true;
  const pawR = pawL.clone();
  pawR.position.x = 0.035;
  group.add(pawL, pawR);

  // Toe pads (tiny pink)
  [-0.035, 0.035].forEach(px => {
    for (let i = -1; i <= 1; i++) {
      const toe = new THREE.Mesh(new THREE.SphereGeometry(0.0045, 10, 8), pinkDark);
      toe.scale.set(1, 0.4, 1);
      toe.position.set(px + i * 0.006, 0.001, 0.142);
      group.add(toe);
    }
  });

  // ---------- Tail (flowing curve around side) ----------
  const tailCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0.04, 0.07, -0.09),
    new THREE.Vector3(0.1, 0.08, -0.08),
    new THREE.Vector3(0.15, 0.12, -0.02),
    new THREE.Vector3(0.17, 0.18, 0.06),
    new THREE.Vector3(0.14, 0.24, 0.12),
    new THREE.Vector3(0.09, 0.26, 0.15),
  ]);
  // taper radius along the curve
  const tailGeo = new THREE.TubeGeometry(tailCurve, 48, 0.022, 14, false);
  const tp = tailGeo.attributes.position;
  const segments = 48 + 1;
  const radial = 14 + 1;
  for (let s = 0; s < segments; s++) {
    const t = s / (segments - 1);
    const taper = 1.0 - t * 0.55;
    for (let r = 0; r < radial; r++) {
      const i = s * radial + r;
      const center = tailCurve.getPoint(t);
      const x = tp.getX(i), y = tp.getY(i), z = tp.getZ(i);
      tp.setX(i, center.x + (x - center.x) * taper);
      tp.setY(i, center.y + (y - center.y) * taper);
      tp.setZ(i, center.z + (z - center.z) * taper);
    }
  }
  tailGeo.computeVertexNormals();
  const tail = new THREE.Mesh(tailGeo, furMain);
  tail.castShadow = true;
  group.add(tail);

  // ---------- Whiskers ----------
  const whiskerMat = new THREE.LineBasicMaterial({ color: 0xF5E6D0, transparent: true, opacity: 0.75 });
  [-1, 1].forEach(side => {
    [-0.006, 0.002, 0.01].forEach((dy, j) => {
      const startX = side * 0.018;
      const startY = 0.215 + dy;
      const startZ = 0.128;
      const endX = side * (0.068 + j * 0.004);
      const endY = startY + (j - 1) * 0.004;
      const endZ = startZ - 0.02;
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(startX, startY, startZ),
        new THREE.Vector3(side * 0.04, startY - 0.001, startZ - 0.008),
        new THREE.Vector3(endX, endY, endZ),
      ]);
      const pts = curve.getPoints(16);
      const g = new THREE.BufferGeometry().setFromPoints(pts);
      group.add(new THREE.Line(g, whiskerMat));
    });
  });

  // Soft ground blob shadow (fake ambient occlusion under the cat)
  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = 128; shadowCanvas.height = 128;
  const sCtx = shadowCanvas.getContext('2d');
  const sg = sCtx.createRadialGradient(64, 64, 10, 64, 64, 60);
  sg.addColorStop(0, 'rgba(0,0,0,0.45)');
  sg.addColorStop(1, 'rgba(0,0,0,0)');
  sCtx.fillStyle = sg;
  sCtx.fillRect(0, 0, 128, 128);
  const shadowTex = new THREE.CanvasTexture(shadowCanvas);
  const shadowMat = new THREE.MeshBasicMaterial({
    map: shadowTex, transparent: true, depthWrite: false, opacity: 0.65,
  });
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(0.35, 0.35), shadowMat);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.set(0, 0.001, 0.02);
  group.add(shadow);

  return group;
}

// Canvas-based text sprite, for FRONT/BACK labels
const spriteCache = new Map();
function makeTextSprite(text, color = '#C25934', bg = 'rgba(255,255,255,0.92)') {
  const key = `${text}|${color}|${bg}`;
  if (spriteCache.has(key)) return spriteCache.get(key).clone();
  const canvas = document.createElement('canvas');
  canvas.width = 512; canvas.height = 160;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = bg;
  const r = 24;
  roundRect(ctx, 0, 0, canvas.width, canvas.height, r);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  roundRect(ctx, 2, 2, canvas.width - 4, canvas.height - 4, r - 2);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.font = 'bold 78px "Cabinet Grotesk", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 4);
  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 8;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  sprite.renderOrder = 10;
  spriteCache.set(key, sprite);
  return sprite.clone();
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// Minimal room outline — only the back wall, a baseboard, a picture, a rug
// and a floor lamp. No side walls, so the scene feels open and easy to orbit.
function buildRoomOutline({ minX, maxX, floorY, ceilingY, backZ, frontZ }) {
  const group = new THREE.Group();
  group.name = 'room-outline';
  group.userData.isReference = true;

  const width = maxX - minX;
  const height = ceilingY - floorY;
  const centerX = (minX + maxX) / 2;
  const centerY = (floorY + ceilingY) / 2;

  const wallMat = new THREE.MeshStandardMaterial({
    color: '#F1E8DB',
    roughness: 0.96, metalness: 0.0,
    side: THREE.DoubleSide,
    transparent: true, opacity: 0.6,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: '#FAF5EB', roughness: 0.7, metalness: 0.0,
  });
  const edgeMat = new THREE.LineBasicMaterial({
    color: 0x8B7A68, transparent: true, opacity: 0.45,
  });

  const backWall = new THREE.Mesh(new THREE.PlaneGeometry(width, height), wallMat);
  backWall.position.set(centerX, centerY, backZ);
  backWall.receiveShadow = true;
  group.add(backWall);
  const edges = new THREE.EdgesGeometry(backWall.geometry);
  const wallOutline = new THREE.LineSegments(edges, edgeMat);
  wallOutline.position.copy(backWall.position);
  group.add(wallOutline);

  const bbH = 0.08;
  const bbT = 0.012;
  const bbBack = new THREE.Mesh(new THREE.BoxGeometry(width, bbH, bbT), trimMat);
  bbBack.position.set(centerX, floorY + bbH / 2, backZ + bbT / 2);
  group.add(bbBack);

  // Picture frame on the back wall (strong cue that this is the far wall)
  const picW = Math.min(0.8, width * 0.3);
  const picH = picW * 0.7;
  const picY = floorY + Math.min(height - picH / 2 - 0.3, height * 0.62);
  const picX = centerX - width * 0.22;
  const frameMat = new THREE.MeshStandardMaterial({ color: '#3D2A1D', roughness: 0.5 });
  const frame = new THREE.Mesh(new THREE.BoxGeometry(picW, picH, 0.02), frameMat);
  frame.position.set(picX, picY, backZ + 0.011);
  group.add(frame);
  // Picture canvas (abstract art via canvas texture)
  const artCanvas = document.createElement('canvas');
  artCanvas.width = 256; artCanvas.height = 180;
  const actx = artCanvas.getContext('2d');
  const g = actx.createLinearGradient(0, 0, 256, 180);
  g.addColorStop(0, '#E8B07A'); g.addColorStop(0.5, '#C25934'); g.addColorStop(1, '#3D2A1D');
  actx.fillStyle = g; actx.fillRect(0, 0, 256, 180);
  actx.fillStyle = 'rgba(255,240,220,0.7)';
  actx.beginPath(); actx.arc(190, 55, 22, 0, Math.PI * 2); actx.fill();
  actx.strokeStyle = 'rgba(20,10,5,0.3)'; actx.lineWidth = 2;
  for (let i = 0; i < 8; i++) {
    actx.beginPath();
    actx.moveTo(0, 100 + i * 10);
    actx.bezierCurveTo(80, 90 + i * 10, 180, 110 + i * 10, 256, 100 + i * 10);
    actx.stroke();
  }
  const artTex = new THREE.CanvasTexture(artCanvas);
  const art = new THREE.Mesh(
    new THREE.PlaneGeometry(picW - 0.05, picH - 0.05),
    new THREE.MeshStandardMaterial({ map: artTex, roughness: 0.8 })
  );
  art.position.set(picX, picY, backZ + 0.022);
  group.add(art);

  // Floor lamp next to the furniture (additional cue — behind furniture corner)
  const lampX = maxX - 0.25;
  const lampZ = backZ + 0.25;
  const lampBase = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.12, 0.015, 24),
    new THREE.MeshStandardMaterial({ color: '#2B2522', roughness: 0.4, metalness: 0.3 })
  );
  lampBase.position.set(lampX, floorY + 0.008, lampZ);
  group.add(lampBase);
  const lampPole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.008, 0.008, 1.55, 12),
    new THREE.MeshStandardMaterial({ color: '#2B2522', roughness: 0.5, metalness: 0.4 })
  );
  lampPole.position.set(lampX, floorY + 0.78, lampZ);
  group.add(lampPole);
  const lampShade = new THREE.Mesh(
    new THREE.ConeGeometry(0.16, 0.22, 24, 1, true),
    new THREE.MeshStandardMaterial({
      color: '#F4E8C9', roughness: 0.8, side: THREE.DoubleSide,
      emissive: '#F4D79A', emissiveIntensity: 0.25,
    })
  );
  lampShade.position.set(lampX, floorY + 1.65, lampZ);
  group.add(lampShade);
  // Warm point-light inside the shade
  const warmLight = new THREE.PointLight('#FFD9A0', 0.35, 2.5, 2.0);
  warmLight.position.set(lampX, floorY + 1.55, lampZ);
  group.add(warmLight);

  // Small rug in front of furniture (gives clear "front" area)
  const rugTex = (() => {
    const c = document.createElement('canvas');
    c.width = 256; c.height = 256;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#C87F58'; ctx.fillRect(0, 0, 256, 256);
    ctx.strokeStyle = '#8E4D2E'; ctx.lineWidth = 3;
    for (let y = 8; y < 256; y += 14) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(256, y + Math.sin(y * 0.1) * 3); ctx.stroke();
    }
    ctx.strokeStyle = '#F4D0A8';
    ctx.strokeRect(6, 6, 244, 244);
    ctx.strokeRect(14, 14, 228, 228);
    return new THREE.CanvasTexture(c);
  })();
  const rugW = Math.min(1.6, width * 0.45);
  const rugD = Math.min(1.0, (frontZ - 0.1) * 0.6);
  const rug = new THREE.Mesh(
    new THREE.PlaneGeometry(rugW, rugD),
    new THREE.MeshStandardMaterial({ map: rugTex, roughness: 0.95, transparent: true, opacity: 0.9 })
  );
  rug.rotation.x = -Math.PI / 2;
  rug.position.set(centerX, floorY + 0.003, frontZ - rugD / 2 - 0.1);
  group.add(rug);

  return group;
}

function getZPosition(mod, maxDepth) {
  const type = mod.type;
  const depth = mod.depth;
  const d = depth * SCALE;
  const md = maxDepth * SCALE;
  const inset = 0.005;
  if (typeof mod.z_pos === 'number') {
    return (mod.z_pos + depth / 2) * SCALE;
  }
  switch (type) {
    case 'back_panel':
      return d / 2;
    case 'door':
      return md - d / 2;
    case 'drawer':
      return Math.max(d / 2 + inset, md - d / 2 - inset);
    case 'shelf': case 'board': case 'hanging_rod': case 'slat':
    case 'crossbar': case 'bracket':
      return md / 2;
    default:
      return md / 2;
  }
}

// Добавляет размерные выноски к корпусу (W x H x D) и отдельным ячейкам-перегородкам
function addTechDimensions(group, modules, ctx) {
  const body = modules.find(m => m.type === 'body' || m.type === 'frame');
  if (!body) return;
  const { cx, cy, cz, maxDepth } = ctx;

  const x0 = body.x * SCALE - cx;
  const x1 = (body.x + body.width) * SCALE - cx;
  const y0 = -(body.y + body.height) * SCALE - cy;
  const y1 = -body.y * SCALE - cy;
  const z0 = maxDepth * SCALE / 2 - cz;      // «перед»
  const z1 = -cz;                             // «зад»

  const lineMat = new THREE.LineBasicMaterial({ color: 0xC25934, transparent: true, opacity: 0.85 });
  const addLine = (points) => {
    const g = new THREE.BufferGeometry().setFromPoints(points.map(p => new THREE.Vector3(...p)));
    const l = new THREE.Line(g, lineMat);
    l.raycast = () => {};
    group.add(l);
  };
  const addLabel = (text, pos, scale = 0.12) => {
    const s = makeTextSprite(text);
    s.position.set(...pos);
    s.scale.set(scale * 3.2, scale, 1);
    group.add(s);
  };

  // Ширина внизу
  const yBelow = y0 - 0.08;
  addLine([[x0, yBelow, z0], [x1, yBelow, z0]]);
  addLine([[x0, y0, z0], [x0, yBelow - 0.03, z0]]);
  addLine([[x1, y0, z0], [x1, yBelow - 0.03, z0]]);
  addLabel(`${Math.round(body.width * 10)} мм`, [(x0 + x1) / 2, yBelow - 0.07, z0]);

  // Высота справа
  const xRight = x1 + 0.08;
  addLine([[xRight, y0, z0], [xRight, y1, z0]]);
  addLine([[x1, y0, z0], [xRight + 0.03, y0, z0]]);
  addLine([[x1, y1, z0], [xRight + 0.03, y1, z0]]);
  addLabel(`${Math.round(body.height * 10)} мм`, [xRight + 0.22, (y0 + y1) / 2, z0], 0.1);

  // Глубина слева
  const xLeft = x0 - 0.08;
  addLine([[xLeft, y0, z0], [xLeft, y0, z1]]);
  addLabel(`${Math.round(body.depth * 10)} мм`, [xLeft - 0.1, y0, (z0 + z1) / 2], 0.1);

  // Выноски на перегородки (вертикальные панели в компиляторе)
  modules.forEach(m => {
    if (m.type !== 'shelf') return;
    const isVertical = m.height > m.width * 3;
    const isHorizontal = m.width > m.height * 3 && m.width > 20;
    if (!isVertical && !isHorizontal) return;
    if (isVertical) {
      const mx = (m.x + m.width / 2) * SCALE - cx;
      const my = -(m.y + m.height) * SCALE - cy - 0.04;
      addLabel(`${Math.round(m.x * 10)}`, [mx, my, z0], 0.07);
    } else if (isHorizontal) {
      const mx = (m.x + m.width) * SCALE - cx + 0.05;
      const my = -(m.y + m.height / 2) * SCALE - cy;
      addLabel(`${Math.round(m.y * 10)}`, [mx, my, z0], 0.07);
    }
  });
}

export default function ThreeDPreview({
  modules, selectedId, snapEnabled,
  onSelect, onUpdateModule, onAddModuleAtPosition, onDeleteModule, onCopyModule, onRotateModule,
  doorsOpen = false, techView = false,
}) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animFrameRef = useRef(null);
  const meshByIdRef = useRef(new Map());

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onUpdateModuleRef = useRef(onUpdateModule);
  onUpdateModuleRef.current = onUpdateModule;
  const onAddRef = useRef(onAddModuleAtPosition);
  onAddRef.current = onAddModuleAtPosition;
  const snapEnabledRef = useRef(snapEnabled);
  snapEnabledRef.current = snapEnabled;
  const modulesRef = useRef(modules);
  modulesRef.current = modules;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const layoutRef = useRef({ cx: 0, cy: 0, cz: 0, locked: false });
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [ctx, setCtx] = useState(null);

  // Init scene
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#F4F1ED');

    const camera = new THREE.PerspectiveCamera(40, container.clientWidth / container.clientHeight, 0.1, 100);
    camera.position.set(3, 2, 4);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    container.appendChild(renderer.domElement);

    // PBR-friendly environment (HDRI-like indoor room lighting)
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envTex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envTex;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.maxPolarAngle = Math.PI * 0.48;
    controls.minDistance = 0.5;
    controls.maxDistance = 10;

    const ambient = new THREE.AmbientLight(0xffffff, 0.35);
    scene.add(ambient);

    const mainLight = new THREE.DirectionalLight(0xffffff, 0.85);
    mainLight.position.set(5, 8, 5);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 1024;
    mainLight.shadow.mapSize.height = 1024;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 20;
    mainLight.shadow.camera.left = -5;
    mainLight.shadow.camera.right = 5;
    mainLight.shadow.camera.top = 5;
    mainLight.shadow.camera.bottom = -5;
    scene.add(mainLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.25);
    fillLight.position.set(-3, 4, -2);
    scene.add(fillLight);

    const grid = new THREE.GridHelper(6, 30, 0xd4d0cb, 0xe8e5e1);
    grid.name = 'floor-grid';
    scene.add(grid);

    const floorGeo = new THREE.PlaneGeometry(8, 8);
    const floorMat = new THREE.ShadowMaterial({ opacity: 0.18 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    floor.name = 'floor';
    scene.add(floor);

    sceneRef.current = scene;
    rendererRef.current = renderer;
    cameraRef.current = camera;
    controlsRef.current = controls;

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const observer = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      pmrem.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  // Rebuild furniture meshes
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const old = scene.getObjectByName('furniture');
    if (old) {
      old.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material.dispose();
        }
      });
      scene.remove(old);
    }
    meshByIdRef.current.clear();

    // Remove previous reference aides (cat only — room is managed separately)
    ['cat-reference', 'human-reference', 'facing-indicator'].forEach(name => {
      const prev = scene.getObjectByName(name);
      if (prev) {
        prev.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
            else child.material.dispose();
          }
        });
        scene.remove(prev);
      }
    });

    if (!modules.length) {
      const grid = scene.getObjectByName('floor-grid');
      const floor = scene.getObjectByName('floor');
      if (grid) grid.position.y = 0;
      if (floor) floor.position.y = 0;
      const cat = buildCatReference();
      cat.position.set(0.6, 0, 0.3);
      cat.rotation.y = -Math.PI / 6;
      scene.add(cat);
      return;
    }

    const group = new THREE.Group();
    group.name = 'furniture';

    const maxDepth = Math.max(...modules.map(m => m.depth), 30);
    let cx, cy;
    let modMinX = Infinity, modMaxX = -Infinity;
    if (layoutRef.current.locked) {
      cx = layoutRef.current.cx;
      cy = layoutRef.current.cy;
      modules.forEach(m => {
        modMinX = Math.min(modMinX, m.x * SCALE);
        modMaxX = Math.max(modMaxX, (m.x + m.width) * SCALE);
      });
    } else {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      modules.forEach(m => {
        minX = Math.min(minX, m.x * SCALE);
        maxX = Math.max(maxX, (m.x + m.width) * SCALE);
        minY = Math.min(minY, -(m.y + m.height) * SCALE);
        maxY = Math.max(maxY, -m.y * SCALE);
      });
      cx = (minX + maxX) / 2;
      cy = (minY + maxY) / 2;
      modMinX = minX;
      modMaxX = maxX;
      layoutRef.current.cx = cx;
      layoutRef.current.cy = cy;
    }
    const cz = maxDepth * SCALE / 2;
    layoutRef.current.cz = cz;

    let minY = Infinity;
    modules.forEach(m => { minY = Math.min(minY, -(m.y + m.height) * SCALE); });

    modules.forEach(mod => {
      const w = mod.width * SCALE;
      const h = mod.height * SCALE;
      const d = mod.depth * SCALE;
      const x = (mod.x + mod.width / 2) * SCALE - cx;
      const y = -(mod.y + mod.height / 2) * SCALE - cy;
      const z = getZPosition(mod, maxDepth) - cz;

      const isSelected = selectedId === mod.id;
      const isBody = BODY_MODULE_TYPES.includes(mod.type) && (mod.type === 'body' || mod.type === 'frame');

      const hasMaterial = !!mod.material;
      const baseColor = isSelected
        ? '#C25934'
        : (hasMaterial ? mod.color_hex : DEFAULT_BODY_COLOR);

      const applyTextureRepeat = (tex) => {
        if (!tex) return null;
        const rep = Math.max(1, Math.round(Math.max(w, h) * 1.3));
        const clone = tex.clone();
        clone.needsUpdate = true;
        clone.wrapS = clone.wrapT = THREE.RepeatWrapping;
        clone.repeat.set(rep, rep);
        clone.colorSpace = THREE.SRGBColorSpace;
        return clone;
      };

      let mat;
      if (mod.material === 'glass') {
        mat = new THREE.MeshPhysicalMaterial({
          color: baseColor,
          transparent: true,
          opacity: isSelected ? 0.55 : 0.28,
          roughness: 0.04, metalness: 0.0,
          transmission: 0.9, ior: 1.45, thickness: 0.02,
        });
      } else if (mod.material === 'metal') {
        mat = new THREE.MeshStandardMaterial({
          color: baseColor,
          metalness: 0.75, roughness: 0.28,
          map: applyTextureRepeat(makeMaterialTexture('metal')),
        });
      } else if (hasMaterial) {
        mat = new THREE.MeshStandardMaterial({
          color: baseColor,
          roughness: mod.material === 'solid_wood' ? 0.62 : 0.7,
          metalness: 0.02,
          map: applyTextureRepeat(makeMaterialTexture(mod.material)),
        });
      } else {
        mat = new THREE.MeshStandardMaterial({
          color: baseColor,
          roughness: 0.85,
          metalness: 0,
        });
      }

      const edgeColor = isSelected
        ? 0xC25934
        : (hasMaterial
            ? new THREE.Color(mod.color_hex).multiplyScalar(0.5).getHex()
            : (isBody ? 0x4A4238 : 0x8A7A60));
      const edgesMat = new THREE.LineBasicMaterial({
        color: edgeColor, transparent: true,
        opacity: isSelected ? 0.98 : (isBody && !hasMaterial ? 0.9 : 0.6),
      });

      if (isBody) {
        // Build carcass from 5 thin panels (back, top, bottom, left, right).
        // Front face is missing entirely, so raycasting passes through the
        // opening and the user can grab modules inside the cabinet.
        const bodyGroup = new THREE.Group();
        bodyGroup.position.set(x, y, z);
        if (mod.rotation) bodyGroup.rotation.y = THREE.MathUtils.degToRad(mod.rotation);
        bodyGroup.userData.moduleId = mod.id;

        const t = Math.min(0.018, Math.min(w, h, d) * 0.05);
        let panelMat;
        if (hasMaterial) {
          mat.side = THREE.DoubleSide;
          panelMat = mat;
        } else {
          panelMat = new THREE.MeshBasicMaterial({ visible: false });
        }

        const addPanel = (geo, px, py, pz) => {
          const p = new THREE.Mesh(geo, panelMat);
          p.position.set(px, py, pz);
          p.receiveShadow = true;
          p.userData.moduleId = mod.id;
          bodyGroup.add(p);
        };
        addPanel(new THREE.BoxGeometry(w, h, t), 0, 0, -d / 2 + t / 2);
        addPanel(new THREE.BoxGeometry(w, t, d - t), 0, -h / 2 + t / 2, t / 2);
        addPanel(new THREE.BoxGeometry(w, t, d - t), 0, h / 2 - t / 2, t / 2);
        addPanel(new THREE.BoxGeometry(t, h - 2 * t, d - t), -w / 2 + t / 2, 0, t / 2);
        addPanel(new THREE.BoxGeometry(t, h - 2 * t, d - t), w / 2 - t / 2, 0, t / 2);

        const silhouette = new THREE.LineSegments(
          new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d)),
          edgesMat
        );
        silhouette.raycast = () => {};
        bodyGroup.add(silhouette);

        group.add(bodyGroup);
        meshByIdRef.current.set(mod.id, bodyGroup);
        return;
      }

      const geo = new THREE.BoxGeometry(w, h, d);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, y, z);
      if (mod.rotation) mesh.rotation.y = THREE.MathUtils.degToRad(mod.rotation);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.moduleId = mod.id;

      const edgesLine = new THREE.LineSegments(new THREE.EdgesGeometry(geo), edgesMat);
      edgesLine.raycast = () => {};
      mesh.add(edgesLine);

      // Анимация открытия дверей и выдвижения ящиков.
      // Для дверей пивот на петле: оборачиваем mesh в группу, сдвигаем внутрь на половину ширины
      // от стороны навеса, группу ставим на петлю и вращаем её.
      let outer = mesh;
      if (doorsOpen && mod.type === 'door' && !mod.flap) {
        const hinge = mod.hinge === 'R' ? 'R' : 'L';
        const pivot = new THREE.Group();
        // пивот на стороне петли
        pivot.position.set(x + (hinge === 'L' ? -w / 2 : w / 2), y, z + d / 2);
        mesh.position.set((hinge === 'L' ? w / 2 : -w / 2), 0, -d / 2);
        pivot.rotation.y = (hinge === 'L' ? -1 : 1) * (Math.PI / 180) * 95;
        pivot.add(mesh);
        outer = pivot;
      } else if (doorsOpen && mod.type === 'door' && mod.flap) {
        // откидная: петли сверху — вращаем относительно верхней грани
        const pivot = new THREE.Group();
        pivot.position.set(x, y + h / 2, z + d / 2);
        mesh.position.set(0, -h / 2, -d / 2);
        pivot.rotation.x = (Math.PI / 180) * 70;
        pivot.add(mesh);
        outer = pivot;
      } else if (doorsOpen && mod.type === 'drawer') {
        // Выдвигаем ящик вперёд на 80% глубины
        const pullout = (mod.depth || 40) * 0.8 * SCALE;
        mesh.position.set(x, y, z + pullout);
        outer = mesh;
      }

      // Handles: doors get a vertical bar near the opening edge, drawers get
      // a horizontal bar slightly above center. Handles are not rendered on
      // carcass, shelves, rails and other internal parts. Also skip the handle
      // when a door covers this part in the 2D plan — such a drawer/door sits
      // behind the outer cabinet door and should not visibly pierce through it.
      const isCoveredByOuterDoor = modules.some(other =>
        other.id !== mod.id &&
        other.type === 'door' &&
        other.x < mod.x + mod.width &&
        other.x + other.width > mod.x &&
        other.y < mod.y + mod.height &&
        other.y + other.height > mod.y
      );

      if (!isCoveredByOuterDoor) {
        if (mod.type === 'door') {
          const isWide = w > h;
          if (isWide) {
            const handle = buildHandle({
              length: Math.min(w * 0.35, 0.18),
              isVertical: false,
              faceDepth: d,
            });
            handle.position.set(0, -h / 2 + 0.04, 0);
            mesh.add(handle);
          } else {
            const handle = buildHandle({
              length: Math.min(h * 0.4, 0.18),
              isVertical: true,
              faceDepth: d,
            });
            handle.position.set(w / 2 - 0.04, 0, 0);
            mesh.add(handle);
          }
        } else if (mod.type === 'drawer') {
          const handle = buildHandle({
            length: Math.min(w * 0.4, 0.2),
            isVertical: false,
            faceDepth: d,
          });
          handle.position.set(0, h * 0.1, 0);
          mesh.add(handle);
        }
      }

      group.add(outer);
      meshByIdRef.current.set(mod.id, mesh);
    });

    // Технический вид: размеры-выноски над корпусом и ключевыми частями
    if (techView) {
      addTechDimensions(group, modules, { cx, cy, cz, maxDepth });
    }

    scene.add(group);

    const grid = scene.getObjectByName('floor-grid');
    const floor = scene.getObjectByName('floor');
    const floorY = minY - cy - 0.002;
    if (grid) grid.position.y = floorY;
    if (floor) floor.position.y = floorY;

    // Ginger cat reference (~30 cm tall), placed well to the right of the furniture
    const cat = buildCatReference();
    const catOffsetX = 0.6;
    const catX = (modMaxX - cx) + catOffsetX;
    cat.position.set(catX, floorY, 0);
    cat.rotation.y = -Math.PI / 6;
    scene.add(cat);


  }, [modules, selectedId, layoutVersion, doorsOpen, techView]);

  // Rebuild room outline — only when the scene anchor stabilises (not during drag).
  // This avoids flicker of walls on every pointermove.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const prev = scene.getObjectByName('room-outline');
    if (prev) {
      prev.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material.dispose();
        }
      });
      scene.remove(prev);
    }
    if (!modules.length) {
      const room = buildRoomOutline({
        minX: -1.8, maxX: 1.8, floorY: 0, ceilingY: 2.6, backZ: -0.9, frontZ: 1.8,
      });
      scene.add(room);
      return;
    }
    const maxDepth = Math.max(...modules.map(m => m.depth), 30);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    modules.forEach(m => {
      minX = Math.min(minX, m.x * SCALE);
      maxX = Math.max(maxX, (m.x + m.width) * SCALE);
      minY = Math.min(minY, -(m.y + m.height) * SCALE);
      maxY = Math.max(maxY, -m.y * SCALE);
    });
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const floorY = minY - cy - 0.002;
    const halfDepth = (maxDepth * SCALE) / 2;
    const furnTop = Math.max(2.6, (maxY - cy) + 0.4);
    const room = buildRoomOutline({
      minX: Math.min(minX - cx - 1.2, -1.6),
      maxX: Math.max(maxX - cx + 1.2, 1.6),
      floorY,
      ceilingY: floorY + furnTop,
      backZ: -halfDepth - 0.15,
      frontZ: halfDepth + 1.5,
    });
    scene.add(room);
  }, [modules.length, layoutVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click / drag / drop / context
  useEffect(() => {
    const renderer = rendererRef.current;
    const camera = cameraRef.current;
    const scene = sceneRef.current;
    const controls = controlsRef.current;
    if (!renderer || !camera || !scene || !controls) return;
    const el = renderer.domElement;

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let downPos = null;
    let drag = null;

    const setPointer = (e) => {
      const rect = el.getBoundingClientRect();
      pointer.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
    };
    const pickModule = () => {
      const group = scene.getObjectByName('furniture');
      if (!group) return null;
      raycaster.setFromCamera(pointer, camera);
      const hits = raycaster.intersectObjects(group.children, true);
      for (const hit of hits) {
        let obj = hit.object;
        while (obj && !obj.userData.moduleId) obj = obj.parent;
        if (obj && obj.userData.moduleId) return { obj, point: hit.point.clone() };
      }
      return null;
    };
    const snap = (v) => snapEnabledRef.current ? Math.round(v / SNAP_SIZE) * SNAP_SIZE : Math.round(v);

    const onDown = (e) => {
      setCtx(null);
      if (e.button !== 0) return;
      downPos = { x: e.clientX, y: e.clientY };
      setPointer(e);
      const picked = pickModule();
      if (!picked) return;
      const pickedMod = modulesRef.current.find(m => m.id === picked.obj.userData.moduleId);
      if (pickedMod && BODY_MODULE_TYPES.includes(pickedMod.type)) {
        return;
      }
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -picked.obj.position.z);
      const offset = new THREE.Vector3().subVectors(picked.obj.position, picked.point);
      drag = { moduleId: picked.obj.userData.moduleId, plane, offset, started: false };
    };

    const onMove = (e) => {
      if (!drag || !downPos) return;
      const dx = e.clientX - downPos.x;
      const dy = e.clientY - downPos.y;
      if (!drag.started) {
        if (Math.sqrt(dx * dx + dy * dy) < 4) return;
        drag.started = true;
        controls.enabled = false;
        layoutRef.current.locked = true;
        setIsDragging(true);
        onSelectRef.current(drag.moduleId);
      }
      setPointer(e);
      raycaster.setFromCamera(pointer, camera);
      const hitPoint = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(drag.plane, hitPoint)) return;
      const newCenter = hitPoint.add(drag.offset);
      const mod = modulesRef.current.find(m => m.id === drag.moduleId);
      if (!mod) return;
      const { cx, cy } = layoutRef.current;
      let newX = (newCenter.x + cx) / SCALE - mod.width / 2;
      let newY = -(newCenter.y + cy) / SCALE - mod.height / 2;
      newX = Math.max(0, snap(newX));
      newY = Math.max(0, snap(newY));
      onUpdateModuleRef.current(drag.moduleId, { x: newX, y: newY });
    };

    const onUp = (e) => {
      const wasDrag = drag && drag.started;
      if (drag && drag.started) {
        controls.enabled = true;
        layoutRef.current.locked = false;
        setIsDragging(false);
        setLayoutVersion(v => v + 1);
      }
      drag = null;
      if (!downPos) return;
      const dx = e.clientX - downPos.x;
      const dy = e.clientY - downPos.y;
      downPos = null;
      if (wasDrag) return;
      if (Math.sqrt(dx * dx + dy * dy) > 4) return;
      if (e.button === 0) {
        setPointer(e);
        const picked = pickModule();
        if (picked) onSelectRef.current(picked.obj.userData.moduleId);
        else onSelectRef.current(null);
      }
    };

    const onContextMenu = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      pointer.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const picked = pickModule();
      if (picked) {
        onSelectRef.current(picked.obj.userData.moduleId);
        setCtx({ x: e.clientX, y: e.clientY, moduleId: picked.obj.userData.moduleId });
      }
    };

    // Drop from library: HTML5 dnd on canvas
    const onDragOver = (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDrop = (e) => {
      e.preventDefault();
      if (!e.dataTransfer) return;
      let data;
      try { data = JSON.parse(e.dataTransfer.getData('application/json')); } catch { return; }
      if (!data) return;

      const rect = el.getBoundingClientRect();
      pointer.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      raycaster.setFromCamera(pointer, camera);
      // Cast onto front plane z = maxDepth*SCALE/2 (center) for consistency with 2D-like coords
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -(layoutRef.current.cz || 0));
      const hit = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      const { cx, cy } = layoutRef.current;
      const defW = data.defaultW || data.width || 50;
      const defH = data.defaultH || data.height || 50;
      let mx = (hit.x + cx) / SCALE - defW / 2;
      let my = -(hit.y + cy) / SCALE - defH / 2;
      mx = Math.max(0, snap(mx));
      my = Math.max(0, snap(my));
      if (onAddRef.current) onAddRef.current(data, mx, my);
    };

    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('dragover', onDragOver);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      el.removeEventListener('contextmenu', onContextMenu);
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('drop', onDrop);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-hidden relative"
      style={{ minHeight: '100%', cursor: isDragging ? 'grabbing' : 'default' }}
      data-testid="3d-preview"
    >
      {modules.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <p className="text-sm text-[#5C5C5C]">Перетащите модуль или выберите шаблон в библиотеке</p>
        </div>
      )}

      <div className="absolute bottom-4 left-4 bg-white/80 backdrop-blur-sm rounded-sm px-3 py-2 border border-black/5 z-10 pointer-events-none">
        <p className="mono-text text-[10px] text-[#5C5C5C]">
          ЛКМ по фону — вращение камеры&nbsp;&nbsp;·&nbsp;&nbsp;ЛКМ по модулю — перетаскивание&nbsp;&nbsp;·&nbsp;&nbsp;Колёсико — зум&nbsp;&nbsp;·&nbsp;&nbsp;ПКМ — меню
        </p>
      </div>

      {/* Scale reference legend */}
      <div className="absolute bottom-4 right-4 bg-white/85 backdrop-blur-sm rounded-sm px-3 py-1.5 border border-black/5 z-10 pointer-events-none flex flex-col gap-1" data-testid="scale-reference">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: '#E8763F' }} />
          <span className="mono-text text-[10px] text-[#5C5C5C]">Котик ~30 см — ориентир масштаба</span>
        </div>
        <div className="flex items-center gap-2 pt-0.5 border-t border-black/10 mt-0.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm border border-[#8B7A68]" />
          <span className="mono-text text-[10px] text-[#5C5C5C]">Мебель стоит у стены — фасад к зрителю</span>
        </div>
      </div>

      {ctx && (
        <ContextMenu
          x={ctx.x} y={ctx.y}
          onClose={() => setCtx(null)}
          onCopy={() => onCopyModule && onCopyModule(ctx.moduleId)}
          onDelete={() => onDeleteModule && onDeleteModule(ctx.moduleId)}
          onRotate={() => onRotateModule && onRotateModule(ctx.moduleId)}
        />
      )}
    </div>
  );
}

